import { useCallback } from "react";
import { FileBarChart, FileSpreadsheet } from "lucide-react";

import { useCustomReports, useStandardReports } from "@/hooks/useCustomReports";

import type { AppRoute } from "@/lib/routes";

/** The "My reports" sidebar row's path: a group key, redirecting to the landing. */
export const MY_REPORTS_PATH = "/reports/custom";
/** The "Standard reports" sidebar row's path, likewise a group key. */
export const STANDARD_REPORTS_PATH = "/reports/standard";

export function customReportPath(id: string): string {
  return `${MY_REPORTS_PATH}/${id}`;
}

/**
 * The Reports sidebar's live rows: the tenant's standard reports under
 * "Standard reports", in the server's order, and the caller's own under "My
 * reports", A–Z by name. A `useRows` hook (`ModuleSection.useRows`), because
 * the lists are the server's and a module declaration is evaluated once.
 *
 * The rows are sidebar decoration; the route they link to,
 * `/reports/custom/:id`, is registered once as a hidden route.
 */
export function useMyReportRows(): (path: string) => AppRoute[] | undefined {
  const { data: own } = useCustomReports();
  const { data: standard } = useStandardReports();
  return useCallback(
    (path: string) => {
      const rows =
        path === MY_REPORTS_PATH && own
          ? [...own].sort((a, b) => a.name.localeCompare(b.name))
          : path === STANDARD_REPORTS_PATH
            ? standard
            : undefined;
      return rows?.map((r) => ({
        path: customReportPath(r.id),
        label: r.name,
        icon: r.format === "spreadsheet" ? FileSpreadsheet : FileBarChart,
        status: "live" as const,
        element: <></>,
        requiredFeature: "report_builder" as const,
      }));
    },
    [own, standard],
  );
}
